/**
 * 对战音效模块 - Web Audio 实时合成
 *  - 光波对冲持续音（loop）→ 模拟两股能量在中线交锋
 *  - 能量球爆开一次性"砰" → 短促低频冲击
 *
 * 必须在用户点击事件后启动（浏览器自动播放策略）
 * 入口：
 *  - getSfx().playBeamLoop() → 7 秒对波持续音
 *  - getSfx().playExplode() → 爆开一次性
 *  - getSfx().stopAll() → 停止所有（清理用）
 */

type OscType = "sine" | "square" | "triangle" | "sawtooth";

interface BeamLoopHandle {
  stop: () => void;
}

class Sfx {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private currentLoops: Array<{ stop: () => void }> = [];

  private ensureCtx(): AudioContext {
    if (!this.ctx) {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctor();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = 0.5;
      this.masterGain.connect(this.ctx.destination);
    }
    if (this.ctx.state === "suspended") {
      void this.ctx.resume();
    }
    return this.ctx;
  }

  /**
   * 光波对冲持续音：
   *  - 双方不同频率的锯齿波在中线"对抗"，制造颤动 / 不稳定的紧张感
   *  - 加轻微 LFO 调制频率
   *  - 持续整个 7 秒对波
   *  - 返回 stop() 用于清理
   */
  playBeamLoop(durationSec = 7): BeamLoopHandle {
    const ctx = this.ensureCtx();
    const t0 = ctx.currentTime;
    const t1 = t0 + durationSec;

    // === 双方能量波：左锯齿 + 右锯齿，频率错开 7Hz 制造颤动 ===
    const oscL = ctx.createOscillator();
    oscL.type = "sawtooth";
    oscL.frequency.setValueAtTime(140, t0); // defender 较低音

    const oscR = ctx.createOscillator();
    oscR.type = "sawtooth";
    oscR.frequency.setValueAtTime(147, t0); // challenger 高 7Hz（增四度，制造紧张）

    // LFO 调制（每秒 ~6 次的频率颤动，模拟能量波动）
    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.setValueAtTime(6, t0);
    const lfoGain = ctx.createGain();
    lfoGain.gain.setValueAtTime(8, t0); // ±8Hz 摆动

    lfo.connect(lfoGain);
    lfoGain.connect(oscL.frequency);
    lfoGain.connect(oscR.frequency);

    // 主增益
    const beamGain = ctx.createGain();
    beamGain.gain.setValueAtTime(0, t0);
    // 缓慢上升
    beamGain.gain.linearRampToValueAtTime(0.18, t0 + 0.4);
    // 持续
    beamGain.gain.setValueAtTime(0.18, t1 - 0.3);
    // 收尾
    beamGain.gain.linearRampToValueAtTime(0, t1);

    // 高通（去掉低频嗡声，保留"嘶嘶"感）
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.setValueAtTime(120, t0);

    // 低通（模拟空间感）
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(2800, t0);

    oscL.connect(beamGain);
    oscR.connect(beamGain);
    beamGain.connect(hp);
    hp.connect(lp);
    lp.connect(this.masterGain!);

    // === 高频嘶嘶（noise + 滤波，做"放电"质感）===
    const bufferSize = ctx.sampleRate * 0.5;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    noise.loop = true;
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = "bandpass";
    noiseFilter.frequency.setValueAtTime(1800, t0);
    noiseFilter.frequency.linearRampToValueAtTime(3500, t1);
    noiseFilter.Q.setValueAtTime(2, t0);
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0, t0);
    noiseGain.gain.linearRampToValueAtTime(0.05, t0 + 0.2);
    noiseGain.gain.setValueAtTime(0.05, t1 - 0.2);
    noiseGain.gain.linearRampToValueAtTime(0, t1);
    noise.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(this.masterGain!);

    oscL.start(t0);
    oscR.start(t0);
    lfo.start(t0);
    noise.start(t0);
    oscL.stop(t1 + 0.05);
    oscR.stop(t1 + 0.05);
    lfo.stop(t1 + 0.05);
    noise.stop(t1 + 0.05);

    const handle: BeamLoopHandle = {
      stop: () => {
        try {
          const now = ctx.currentTime;
          beamGain.gain.cancelScheduledValues(now);
          beamGain.gain.setValueAtTime(beamGain.gain.value, now);
          beamGain.gain.linearRampToValueAtTime(0, now + 0.1);
          noiseGain.gain.cancelScheduledValues(now);
          noiseGain.gain.setValueAtTime(noiseGain.gain.value, now);
          noiseGain.gain.linearRampToValueAtTime(0, now + 0.1);
          oscL.stop(now + 0.15);
          oscR.stop(now + 0.15);
          lfo.stop(now + 0.15);
          noise.stop(now + 0.15);
        } catch {
          /* ignore */
        }
      },
    };
    this.currentLoops.push(handle);
    return handle;
  }

  /**
   * 能量球爆开一次性"砰"：
   *  - 0 ~ 0.1s 强低频冲击（sine 60→20Hz sweep）
   *  - 0 ~ 0.3s 噪声短促爆发（白噪 + 低通快速收）
   *  - 高频闪光"叮"（短三角波）
   * 音量比之前更响：kick 0.9 / noise 0.8 / ping 0.45
   */
  playExplode(): void {
    const ctx = this.ensureCtx();
    const t0 = ctx.currentTime;

    // 1. 低频冲击（加大音量）
    const kick = ctx.createOscillator();
    kick.type = "sine";
    kick.frequency.setValueAtTime(120, t0);
    kick.frequency.exponentialRampToValueAtTime(20, t0 + 0.15);
    const kickGain = ctx.createGain();
    kickGain.gain.setValueAtTime(0, t0);
    kickGain.gain.linearRampToValueAtTime(0.95, t0 + 0.005);
    kickGain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.3);
    kick.connect(kickGain);
    kickGain.connect(this.masterGain!);
    kick.start(t0);
    kick.stop(t0 + 0.32);

    // 2. 噪声爆发（加大音量）
    const bufferSize = ctx.sampleRate * 0.35;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = "lowpass";
    noiseFilter.frequency.setValueAtTime(4000, t0);
    noiseFilter.frequency.exponentialRampToValueAtTime(500, t0 + 0.3);
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0, t0);
    noiseGain.gain.linearRampToValueAtTime(0.85, t0 + 0.01);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.35);
    noise.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(this.masterGain!);
    noise.start(t0);
    noise.stop(t0 + 0.36);

    // 3. 高频闪光（加大音量）
    const ping = ctx.createOscillator();
    ping.type = "triangle";
    ping.frequency.setValueAtTime(2400, t0);
    ping.frequency.exponentialRampToValueAtTime(900, t0 + 0.18);
    const pingGain = ctx.createGain();
    pingGain.gain.setValueAtTime(0, t0);
    pingGain.gain.linearRampToValueAtTime(0.5, t0 + 0.005);
    pingGain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.22);
    ping.connect(pingGain);
    pingGain.connect(this.masterGain!);
    ping.start(t0);
    ping.stop(t0 + 0.24);
  }

  /**
   * 按钮点击清脆"叮"声（清脆、不复杂）：
   *  - 主振 sine 2.4kHz（高频泛音 = 清脆感）
   *  - 二次谐波 sine 4.8kHz（增强金属感）
   *  - 总时长仅 0.08s（短促）
   *  - 音量适中 0.15（清脆但不刺耳）
   */
  playClick(): void {
    const ctx = this.ensureCtx();
    const t0 = ctx.currentTime;

    // 主振 2.4kHz
    const osc1 = ctx.createOscillator();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(2400, t0);
    osc1.frequency.exponentialRampToValueAtTime(1800, t0 + 0.05);

    // 二次谐波 4.8kHz（清脆泛音）
    const osc2 = ctx.createOscillator();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(4800, t0);
    osc2.frequency.exponentialRampToValueAtTime(3600, t0 + 0.05);

    // 主增益（包络：瞬态 attack + 快速 release）
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(0.15, t0 + 0.003);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.08);

    // 谐波稍弱
    const harmGain = ctx.createGain();
    harmGain.gain.setValueAtTime(0, t0);
    harmGain.gain.linearRampToValueAtTime(0.08, t0 + 0.003);
    harmGain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.08);

    osc1.connect(gain);
    osc2.connect(harmGain);
    gain.connect(this.masterGain!);
    harmGain.connect(this.masterGain!);

    osc1.start(t0);
    osc2.start(t0);
    osc1.stop(t0 + 0.09);
    osc2.stop(t0 + 0.09);
  }

  /**
   * 停止所有正在循环的音（清理）
   */
  stopAll(): void {
    for (const h of this.currentLoops) {
      try { h.stop(); } catch { /* ignore */ }
    }
    this.currentLoops = [];
  }
}

let _instance: Sfx | null = null;
export function getSfx(): Sfx {
  if (!_instance) _instance = new Sfx();
  return _instance;
}

/**
 * 兼容旧 API：保留 getBgm（防止 import 残留下 typecheck 错误）
 * 但实际不再返回 Chiptune。调用方应迁移到 getSfx。
 */
export function getBgm(): Sfx {
  return getSfx();
}